'use client';

import type { ContactFormViewModel } from '../form.types';
import { Button, Field, Fieldset, Input, Label, Legend, Radio, RadioGroup, Select, Textarea } from '@headlessui/react';
import Form from 'next/form';
import { useActionState } from 'react';
import type { ContactFormState } from '@/features/forms/actions/contact-form';
import { submitContactForm } from '@/features/forms/actions/contact-form';
import { useFillDuration } from '@/features/forms/useFillDuration';
import { FormSpamGuard } from './FormSpamGuard';

export const initialContactFormState: ContactFormState = {
    ok: false,
    values: {
        personType: 'owner',
        email: '',
        subject: '',
        message: '',
        unit: '',
        object: '',
    },
    errors: {},
};

export function ContactForm({ className }: { form: ContactFormViewModel; className?: string }) {
    const [state, action, pending] = useActionState(submitContactForm, initialContactFormState);
    const stampFillDuration = useFillDuration();

    // React hands the action the FormData it built from the DOM — the only place to
    // add the fill duration before the server action sees it.
    const submit = (formData: FormData) => {
        stampFillDuration(formData);
        action(formData);
    };

    return (
        <Form action={submit} className={className}>
            <Fieldset>
                <Legend className="text-display-xs font-light uppercase mb-9">Teilen Sie uns Ihr Anliegen mit.</Legend>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="md:col-span-2">
                        <span className="form-label">Ich bin</span>

                        <RadioGroup
                            name="personType"
                            defaultValue={state?.values?.personType}
                            className="form-radio-group"
                        >
                            <Field className="form-radio-field">
                                <Radio value="owner" className="group form-radio">
                                    <span className="form-radio-dot" />
                                </Radio>
                                <Label className="form-radio-label">Eigentümer</Label>
                            </Field>

                            <Field className="form-radio-field">
                                <Radio value="tenant" className="group form-radio">
                                    <span className="form-radio-dot" />
                                </Radio>
                                <Label className="form-radio-label">Mieter</Label>
                            </Field>
                        </RadioGroup>
                    </div>

                    <Field>
                        <Label className="form-label">Objekt</Label>
                        <Select
                            name="object"
                            className="form-select"
                            defaultValue={state?.values?.object}
                            invalid={!!state?.errors?.object}
                        >
                            <option value="">Objekt auswählen</option>
                            <option value="1">One</option>
                            <option value="2">Two</option>
                            <option value="3">Three</option>
                        </Select>
                    </Field>

                    <Field>
                        <Label className="form-label">Einheit</Label>
                        <Select name="unit" className="form-select" defaultValue={state?.values.unit}>
                            <option value="">Einheit auswählen</option>
                            <option value="1">One</option>
                            <option value="2">Two</option>
                            <option value="3">Three</option>
                        </Select>
                    </Field>

                    <Field>
                        <Label className="form-label">Email</Label>
                        <Input
                            name="email"
                            type="email"
                            defaultValue={state?.values?.email}
                            placeholder="Ihre Email Adresse ..."
                            className="form-input"
                            invalid={!!state?.errors?.email}
                        />

                        {state?.errors?.email && (
                            <p role="alert" className="mt-2 text-xs text-red-400">
                                {state.errors.email}
                            </p>
                        )}
                    </Field>

                    <Field>
                        <Label className="form-label">Anliegen</Label>
                        <Input
                            name="subject"
                            type="text"
                            defaultValue={state?.values?.subject}
                            invalid={!!state?.errors?.subject}
                            placeholder="Ihr Anliegen ..."
                            className="form-input"
                        />

                        {state?.errors?.subject && (
                            <p role="alert" className="mt-2 text-xs text-red-400">
                                {state.errors.subject}
                            </p>
                        )}
                    </Field>

                    <Field className="md:col-span-2">
                        <Label className="form-label">Anliegen</Label>
                        <Textarea
                            name="message"
                            defaultValue={state?.values?.message}
                            className="form-input"
                            rows={3}
                            invalid={!!state?.errors?.message}
                        />

                        {state?.errors?.message && (
                            <p role="alert" className="mt-2 text-xs text-red-400">
                                {state.errors.message}
                            </p>
                        )}
                    </Field>
                </div>

                <div className="mt-8 flex justify-end">
                    <Button type="submit" className="btn" disabled={pending}>
                        Nachricht absenden
                    </Button>
                </div>
            </Fieldset>
            <FormSpamGuard />
        </Form>
    );
}
